import { uniqueId } from "lodash";
import * as React from "react";
import { useContext, useEffect, useState } from "react";
import { icon, Icon } from "~/sharedComponents/icons";
import { Box } from "~/sharedComponents/layout";
import { MenuContext } from "~/sharedComponents/menu/MenuContext";
import { MenuItem, MenuItemProps } from "~/sharedComponents/menu/MenuItem";
import { Popover, PopupContentProps } from "~/sharedComponents/popover";
import * as styles from "./Menu.less";

type SubMenuProps = MenuItemProps & {
    renderSubMenu: (props: PopupContentProps) => React.ReactElement;
};

export const SubMenuItem: React.FC<SubMenuProps> = ({ renderSubMenu, ...props }) => {
    const [id] = useState(() => uniqueId("menuitem_"));
    const { isOpened, setIsOpened } = useIsSubMenuOpened(id);

    return (
        <>
            <Popover
                renderContent={renderSubMenu}
                isOpen={isOpened}
                onToggle={setIsOpened} // just to support toggle sub menu by keyboard (enter/esc)
                wrapperClassName={Popover.BLOCK_WRAPPER_CLASS}
                blockClicksOutside={false}
                placement={"right-start"}
                showWithoutIndent={true}
            >
                <MenuItem ignoreClose={true} menuId={id} isActive={isOpened} {...props}>
                    <Box marginLeft={"auto"} paddingLeft={8}>
                        <Icon iconName={icon.chevron_right_small} />
                    </Box>
                </MenuItem>
            </Popover>
            <p className={styles.menu_item_marker} />
        </>
    );
};

const useIsSubMenuOpened = (menuItemId: string) => {
    const { setOpenedSubMenu, openedSubMenuId, hoveredMenuItemId } = useContext(MenuContext);

    const isOpened = openedSubMenuId === menuItemId;

    const isCurrentItemHovered = hoveredMenuItemId === menuItemId;
    const isAnotherMenuHovered = !!hoveredMenuItemId && hoveredMenuItemId !== menuItemId;

    const setIsOpened = (isOpened: boolean) => setOpenedSubMenu(isOpened ? menuItemId : null);

    useEffect(() => {
        // open submenu only when another one submenu was closed.
        if (isCurrentItemHovered && !openedSubMenuId) {
            setIsOpened(true);
        }
    }, [isCurrentItemHovered, openedSubMenuId]);

    // close current submenu if another menu item is hovered.
    // use delay to allow user move mouse by diagonally to submenu item (with hovering another menu items)
    useEffect(() => {
        if (isOpened && isAnotherMenuHovered) {
            const timer = setTimeout(() => setIsOpened(false), 300);
            return () => clearTimeout(timer);
        }
    }, [isAnotherMenuHovered, isOpened]);

    return { isOpened, setIsOpened };
};
